import React from 'react';
import { cn } from '../lib/utils';
import { env, parseBrandDisplay } from '../config/env';

type BrandLogoVariant = 'mark' | 'full';
/** auto = follows theme; light = white (for colored/dark panels) */
type BrandLogoTone = 'auto' | 'light';

interface BrandLogoProps {
  variant?: BrandLogoVariant;
  tone?: BrandLogoTone;
  className?: string;
  alt?: string;
  showTagline?: boolean;
}

const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const asset = (path: string) => `${publicUrl}${path}`;

const MARK_SRC = asset('/logo/converse-handshake.png');

/**
 * Converse.AI brand mark (handshake) or full lockup (mark + CSS wordmark).
 * Mark is black ink on transparent; CSS filters flip for light/dark surfaces.
 */
export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = 'mark',
  tone = 'auto',
  className,
  alt = env.companyName,
  showTagline = true,
}) => {
  const { head, accent } = parseBrandDisplay(env.companyName);
  const filterClass =
    tone === 'light' ? 'brightness-0 invert' : 'brightness-0 dark:invert';

  if (variant === 'mark') {
    return (
      <img
        src={MARK_SRC}
        alt={alt}
        className={cn(
          'h-8 w-8 object-contain object-center',
          filterClass,
          className,
        )}
      />
    );
  }

  return (
    <div
      className={cn('flex flex-col items-center gap-0 text-foreground', className)}
      role="img"
      aria-label={alt}
    >
      <img
        src={MARK_SRC}
        alt=""
        aria-hidden
        className={cn(
          'h-12 w-auto max-w-[220px] object-contain object-center sm:h-14',
          filterClass,
        )}
      />
      <div className="-mt-0.5 text-center">
        <div className="text-3xl font-bold tracking-tight sm:text-4xl">
          {head}
          {accent ? <span className="text-primary">{accent}</span> : null}
        </div>
        {showTagline && (
          <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground sm:text-xs">
            Where empathy meets intelligence
          </p>
        )}
      </div>
    </div>
  );
};
